/**
 * Regra ÚNICA de "este usuário alcança este módulo?".
 *
 * Existia só dentro do `Sidebar`, então qualquer outro ponto do app que
 * precisasse da mesma pergunta inventava a sua própria — foi assim que o
 * `loadBadges` acabou buscando números financeiros para todo usuário
 * autenticado, sem checar nada, e mostrando ZERO a quem tomava 403.
 *
 * `module` é o código SEM sufixo: a checagem cobre `<module>:view` e
 * `<module>:view_own`. Admin passa por cima de tudo.
 */

export const hasPermission = (user: any, permissionCode: string): boolean => {
  if (user?.role === 'admin') return true;
  return user?.permissions?.some((p: any) => p.code === permissionCode) || false;
};

export const canAccessModule = (user: any, module: string): boolean => {
  if (user?.role === 'admin') return true;
  return hasPermission(user, `${module}:view`) || hasPermission(user, `${module}:view_own`);
};

/** Versão presa a um usuário, no formato que o filtro do menu espera. */
export const moduleAccessFor = (user: any) => (module: string) => canAccessModule(user, module);

/**
 * ===== Terceira camada: BOTÕES DE AÇÃO =====
 *
 * O menu diz o que aparece e as rotas dizem quem executa. Faltava a tela parar
 * de OFERECER a ação: editor continuava com Editar/Duplicar/Excluir em Produtos
 * e DDRs, e só descobria o 403 ao clicar.
 *
 * A raiz era um booleano único no App.tsx — `canEdit = role admin ou editor` —
 * passado igual para TODAS as tabelas, sem distinguir módulo. O mapa abaixo
 * espelha, módulo a módulo, o que o backend aceita de fato; levantado lendo os
 * controllers em 0a0ff7d, decorator por decorator.
 *
 * ⚠️ Esconder botão NÃO é proteção — é cortesia com quem usa a tela. Quem
 * protege é o @Roles do backend. Módulo cujo backend ainda esteja ABERTO não
 * entra aqui como se estivesse fechado: sumir da tela e seguir exposto pela API
 * é pior que o problema original, porque vira falsa sensação de segurança.
 *
 * Módulo não listado mantém o comportamento de hoje (admin ou editor). Esta
 * passada é de ENDURECIMENTO: só pode tirar botão de quem não podia clicar,
 * nunca dar botão a quem não tinha.
 */
type Acao = 'write' | 'delete';

const ESCRITA_POR_MODULO: Record<string, Record<Acao, string[]>> = {
  // 100% ADMIN no backend (nº de rotas de escrita conferido em 0a0ff7d)
  products: { write: ['admin'], delete: ['admin'] },            // 4
  ddrs: { write: ['admin'], delete: ['admin'] },                // 12
  contracts: { write: ['admin'], delete: ['admin'] },           // 17
  invoices: { write: ['admin'], delete: ['admin'] },            // 35
  'digital-contracts': { write: ['admin'], delete: ['admin'] }, // 9
  reajustes: { write: ['admin'], delete: ['admin'] },           // 9
  'bank-accounts': { write: ['admin'], delete: ['admin'] },     // 8
  'loan-contracts': { write: ['admin'], delete: ['admin'] },    // 8
  expenses: { write: ['admin'], delete: ['admin'] },            // 28
  // Misto: editor cria e edita, só admin apaga
  clients: { write: ['admin', 'editor'], delete: ['admin'] },
  // Editor segue podendo
  leads: { write: ['admin', 'editor', 'vendedor'], delete: ['admin', 'editor', 'vendedor'] },
  materials: { write: ['admin', 'editor'], delete: ['admin', 'editor'] },
};

const PADRAO: Record<Acao, string[]> = { write: ['admin', 'editor'], delete: ['admin', 'editor'] };

const podeAgir = (user: any, module: string, acao: Acao): boolean => {
  if (user?.role === 'admin') return true;
  const regra = ESCRITA_POR_MODULO[module] || PADRAO;
  return regra[acao].includes(String(user?.role || ''));
};

/** Pode criar/editar neste módulo? */
export const canWriteModule = (user: any, module: string): boolean => podeAgir(user, module, 'write');

/** Pode excluir? Separado porque há módulo em que editor edita mas não apaga. */
export const canDeleteModule = (user: any, module: string): boolean => podeAgir(user, module, 'delete');
